import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useRegistration } from '../../../contexts/RegistrationContext';
import { getCurrentApiEndpoints } from '../../../config/api';

const StepContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #000;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background: #000;
    color: white;
    
    &:hover:not(:disabled) {
      background: #333;
      transform: translateY(-1px);
    }
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

interface BusinessActivityOption {
  code: string;
  name: string;
}

const BusinessActivity: React.FC = () => {
  const { registrationData, setCurrentStep, saveCompanyType, saveBusinessActivity } = useRegistration();
  const [activities, setActivities] = useState<BusinessActivityOption[]>([]);
  const [companyType, setCompanyType] = useState(registrationData.company_type || '');
  const [country, setCountry] = useState(registrationData.country || '');
  const [selectedActivity, setSelectedActivity] = useState(registrationData.business_activity_code || '');
  const [description, setDescription] = useState(registrationData.business_description || '');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const endpoints = getCurrentApiEndpoints();
        const response = await axios.get(endpoints.BUSINESS_ACTIVITIES);
        setActivities(response.data);
      } catch (error) {
        console.error('Failed to fetch business activities:', error);
      }
    };
    
    fetchActivities();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Save company type and country first
      await saveCompanyType({
        company_type: companyType,
        country: country,
      });
      
      // Then save business activity
      const selectedActivityData = activities.find(a => a.code === selectedActivity);
      await saveBusinessActivity({
        business_activity_code: selectedActivity,
        business_activity: selectedActivityData?.name || '',
        business_description: description,
      });
      setCurrentStep(2);
    } catch (error) {
      console.error('Failed to save business activity:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StepContainer>
      <Title>Step 1: Business Activity Details</Title>
      <Description>
        Select your primary business activity from the NACE classification 
        and provide a brief description of your business.
      </Description>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Company Type (Required) *</Label>
          <Select
            value={companyType}
            onChange={(e) => setCompanyType(e.target.value)}
            required
          >
            <option value="">Select company type...</option>
            <option value="LLC">LLC (Limited Liability Company)</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Country (Required) *</Label>
          <Select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            required
          >
            <option value="">Select country...</option>
            <option value="Armenia">Armenia</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Business Activity (Required) *</Label>
          <Select
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e.target.value)}
            required
          >
            <option value="">Select a business activity...</option>
            {activities.map((activity) => (
              <option key={activity.code} value={activity.code}>
                {activity.code} - {activity.name}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Business Description (Required) *</Label>
          <TextArea
            placeholder="Describe your business activity briefly, similar to a tweet..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={280}
            required
          />
          <small style={{ color: '#666' }}>
            {description.length}/280 characters
          </small>
        </FormGroup>

        <ButtonGroup>
          <Button type="submit" className="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Next: Company Name'}
          </Button>
        </ButtonGroup>
      </Form>
    </StepContainer>
  );
};

export default BusinessActivity;